import React, { FC } from 'react'

import { EuiModal, EuiModalHeader, EuiModalHeaderTitle, EuiModalBody, EuiProgress, EuiSpacer, EuiText, EuiCallOut, EuiModalFooter, EuiButton, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { MultiTabPdfProgress } from 'services/multiTabPdfService'

export interface MultiTabPdfProgressModalProps {
  isStaffPdf: boolean
  isVisible: boolean
  progress: MultiTabPdfProgress | null
  error: string | null
  onClose: () => void
  onRetry?: () => void
  closeAutomatically?: boolean
}

const MultiTabPdfProgressModal: FC<MultiTabPdfProgressModalProps> = ({ isStaffPdf, isVisible, progress, error, onClose, onRetry, closeAutomatically }) => {
  if (!isVisible) return null

  const getProgressPercentage = () => {
    if (!progress) return 0
    return Math.round((progress.currentStep / progress.totalSteps) * 100)
  }

  const getProgressText = () => {
    if (error) {
      return 'Generation failed'
    }

    if (!progress) {
      return 'Initializing...'
    }

    if (progress.isComplete) {
      if (closeAutomatically && (!progress.errors || progress.errors.length <= 0)) {
        setTimeout(() => {
          onClose()
        }, 500)
      }

      return 'Complete!'
    }

    return `Generating ${progress.currentTab}...`
  }

  const showCloseButton = error || (progress?.isComplete ?? false)

  return (
    <EuiModal onClose={onClose} style={{ width: '500px' }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <h2>Generating All {isStaffPdf ? 'Staff' : 'Client'} PDFs</h2>
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiText size='s'>
          <p>{getProgressText()}</p>
        </EuiText>

        <EuiSpacer size='m' />

        <EuiProgress value={getProgressPercentage()} max={100} color={error ? 'danger' : progress?.isComplete ? 'success' : 'primary'} size='m' />

        <EuiSpacer size='m' />

        {error && (
          <>
            <EuiCallOut title='PDF Generation Failed' color='danger' iconType='alert' size='s'>
              <p>{error}</p>
              {progress?.errors && progress.errors.length > 0 && (
                <div>
                  <EuiSpacer size='s' />
                  <EuiText size='xs'>
                    <strong>Details:</strong>
                    <ul>
                      {progress.errors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </EuiText>
                </div>
              )}
            </EuiCallOut>
            <EuiSpacer size='m' />
          </>
        )}

        {progress?.isComplete && !error && progress.errors && progress.errors.length > 0 && (
          <>
            <EuiCallOut title='PDF Generated Successfully' color='success' iconType='check' size='s'>
              <p>Your complete campaign plan PDF has been generated and downloaded.</p>
              {progress.errors && progress.errors.length > 0 && (
                <div>
                  <EuiSpacer size='s' />
                  <EuiText size='xs'>
                    <strong>Note:</strong> Some sections had issues but the PDF was still created:
                    <ul>
                      {progress.errors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </EuiText>
                </div>
              )}
            </EuiCallOut>
            <EuiSpacer size='m' />
          </>
        )}
      </EuiModalBody>

      {showCloseButton && (
        <EuiModalFooter>
          <EuiFlexGroup justifyContent='flexEnd'>
            {error && onRetry && (
              <EuiFlexItem grow={false}>
                <EuiButton color='primary' onClick={onRetry}>
                  Retry
                </EuiButton>
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton onClick={onClose} fill={!error}>
                Close
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiModalFooter>
      )}
    </EuiModal>
  )
}

export default MultiTabPdfProgressModal
